import React, { useMemo } from "react";
import { bool, number, string, func } from "prop-types";
import { twMerge } from "tailwind-merge";

import { useTranslation } from "@lib/i18n";

import CashPriceDetail from "./CashPriceDetail";
import FinancePriceDetail from "./FinancePriceDetail";
import VACPriceDetailv2 from "./VACPriceDetailv2";
import Parallelograms from "./Parallelograms";
import JourneySwitch from "./JourneySwitch";
import FinanceWidgetButton from "./FinanceWidgetButton";
import FinanceWidgetButtonUIv2 from "./FinanceWidgetButtonUIv2";
import CircularLoader from "@components/shared/Icons/CircularLoader";
import LOAPriceDetail from "@components/PriceDetail/LOAPriceDetail";
import VACPriceDetail from "@components/PriceDetail/VACPriceDetail";
import SCFPriceDetail from "@components/PriceDetail/SCFPriceDetail";

import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";
import { getIsLOAJourneySelected } from "@shared/v2/lib/getIsLOAJourneySelected";
import { getIsVACJourneySelected } from "@shared/v2/lib/getIsVACJourneySelected";
import { financeGatewayTeaserType } from "@types";
import { useDetectDevice } from "@hooks/useDetectDevice";
import { useIsJourneySwitchEnabled } from "@hooks/useIsJourneySwitchEnabled";
import { useIsUIv2Selector } from "@redux/reducers/appConfig";

import { getIsCountry, COUNTRIES } from "lib/getIsCountry";
import { getIsSCFJourneySelected } from "@shared/v2/lib/getIsSCFJourneySelected";
import { LOCALES } from "@shared/constants";
import { useIsB2BLeasysSelected } from "@redux/reducers/deal";
import { useIsKeyAccount } from "@redux/reducers/auth";

const PriceDetail = ({
  amount,
  className,
  deposit,
  currencySymbol,
  forceFinancingSwitch = false,
  isBasketUi,
  isFinanceRequestError,
  isLoading,
  isPersonalisedVACOffer,
  isQuantityVisible,
  journey,
  priceFormat,
  onIsFinancingOpenChange,
  teaser,
  total = amount,
  vehicleQuantity,
  withDeliveryInfo,
  withEcoBonus,
  withFinanceWidgetButton,
  withJourneySwitch,
  withParallelograms,
  withTitle,
}) => {
  const { i18n, t } = useTranslation();

  const { isMobile } = useDetectDevice();

  const isCashJourneySelected = getIsCashJourneySelected(journey);
  const isLOAJourneySelected = getIsLOAJourneySelected(journey);
  const isVACJourneySelected = getIsVACJourneySelected(journey);
  const isSCFJourneySelected = getIsSCFJourneySelected(journey);

  const isUIv2 = useIsUIv2Selector();

  const isPriceSwitcherEnabled = useIsJourneySwitchEnabled();

  const isKeyAccount = useIsKeyAccount();
  const isB2BLeasysSelected = useIsB2BLeasysSelected();

  const isPriceSwitcherVisible =
    ((!isUIv2 || forceFinancingSwitch) &&
      isPriceSwitcherEnabled &&
      withJourneySwitch) ||
    isKeyAccount;

  const isPriceInitialized =
    Number.isFinite(amount) || isLoading || isFinanceRequestError;

  const isLoadingOrInitializing =
    isLoading || (!isCashJourneySelected && !isPriceInitialized);

  const isFinanceButtonVisible =
    withFinanceWidgetButton &&
    !isSCFJourneySelected &&
    !isLoadingOrInitializing;

  const title = useMemo(() => {
    if (withTitle) {
      if (isCashJourneySelected && i18n.exists("price.yourCashPrice")) {
        return t("price.yourCashPrice");
      }

      if (!isCashJourneySelected && i18n.exists("price.yourFinancePrice")) {
        return t("price.yourFinancePrice");
      }
    }

    return null;
  }, [i18n, isCashJourneySelected, t, withTitle]);

  const getContent = () => {
    if (isLoadingOrInitializing) {
      return (
        <div className="flex flex-1-1-0 justify-center items-center">
          <CircularLoader className="text-white size-12" />
        </div>
      );
    }

    if (isSCFJourneySelected) {
      return <SCFPriceDetail isFinanceRequestError={isFinanceRequestError} />;
    }

    if (isCashJourneySelected || (isKeyAccount && isB2BLeasysSelected)) {
      return (
        <div className="ml-auto flex-1-1-0">
          <CashPriceDetail
            amount={amount}
            currencySymbol={currencySymbol}
            isQuantityVisible={isQuantityVisible}
            isUIv2={isUIv2}
            vehicleQuantity={vehicleQuantity}
            priceFormat={priceFormat}
            withDeliveryInfo={withDeliveryInfo}
            withEcoBonus={withEcoBonus}
            withTitle={!withJourneySwitch}
          />
        </div>
      );
    }

    if (isLOAJourneySelected) {
      return (
        <div className="flex-1-1-0">
          <LOAPriceDetail
            amount={amount}
            teaser={teaser}
            withFinanceWidgetButton={withFinanceWidgetButton}
          />
        </div>
      );
    }

    if (isVACJourneySelected) {
      if (isUIv2) {
        return (
          <div className="flex-1-1-0">
            <div className="flex justify-between sm:justify-end">
              <div
                className={twMerge(
                  "flex flex-col justify-center",
                  isBasketUi && "smallMobile:items-center"
                )}
              >
                <CashPriceDetail
                  amount={total}
                  currencySymbol={currencySymbol}
                  isQuantityVisible={isQuantityVisible}
                  isUIv2={isUIv2}
                  priceFormat={priceFormat}
                  vehicleQuantity={vehicleQuantity}
                  withDeliveryInfo={withDeliveryInfo}
                  withEcoBonus={withEcoBonus}
                  withTitle={!withJourneySwitch}
                />
              </div>
              <div className="mx-2 border-r border-r-white h-auto sm:mx-5 lg:mx-2.5" />
              <VACPriceDetailv2
                isFinanceRequestError={isFinanceRequestError}
                amount={amount}
                apr={teaser?.data.apr}
                currencySymbol={currencySymbol}
                deposit={deposit}
                isBasketUi={isBasketUi}
                isPersonalised={isPersonalisedVACOffer}
                isQuantityVisible={isQuantityVisible}
                isUIv2={isUIv2}
                priceFormat={priceFormat}
                vehicleQuantity={vehicleQuantity}
                withDeliveryInfo={withDeliveryInfo}
                withEcoBonus={withEcoBonus}
                withFinanceWidgetButton={withFinanceWidgetButton}
                withTitle={!withJourneySwitch}
              />
            </div>
          </div>
        );
      }

      return (
        <div className="flex-1-1-0">
          <div className="flex justify-between sm:justify-end">
            <VACPriceDetail
              amount={amount}
              currencySymbol={currencySymbol}
              isFinanceRequestError={isFinanceRequestError}
              onIsFinancingOpenChange={onIsFinancingOpenChange}
              priceFormat={priceFormat}
              teaser={teaser}
              total={total}
              withDeliveryInfo={withDeliveryInfo}
              withEcoBonus={withEcoBonus}
              withFinanceWidgetButton={withFinanceWidgetButton}
              withTitle={!withJourneySwitch}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex-1-1-0">
        <FinancePriceDetail
          amount={amount}
          currencySymbol={currencySymbol}
          deposit={deposit}
          isFinanceRequestError={isFinanceRequestError}
          isQuantityVisible={isQuantityVisible}
          priceFormat={priceFormat}
          vehicleQuantity={vehicleQuantity}
          withDeliveryInfo={withDeliveryInfo}
          withEcoBonus={withEcoBonus}
          withFinanceWidgetButton={withFinanceWidgetButton}
          withTitle={!withJourneySwitch}
        />
      </div>
    );
  };

  const isParallelogramsTemporarilyDisabled =
    (getIsCountry(COUNTRIES.BE) && i18n.language === LOCALES.NL_BE) ||
    (getIsCountry(COUNTRIES.FR) && !isCashJourneySelected) ||
    getIsCountry(COUNTRIES.IT);

  const containerClassName = ["flex flex-1 items-center flex-row"];

  if (withParallelograms && !isBasketUi) {
    containerClassName.push("p-0 sm:pr-12 max-lg:py-2");

    if (isParallelogramsTemporarilyDisabled) {
      containerClassName.push("min-lg:max-[67rem]:pr-0");

      if (getIsCountry(COUNTRIES.IT)) {
        containerClassName.push("min-lg:max-[67rem]:-ml-4");
      }
    }
  } else {
    containerClassName.push("p-0");
  }

  if (isBasketUi || getIsCountry(COUNTRIES.GB)) {
    containerClassName.push("flex-col justify-start gap-y-2");

    if (!isBasketUi) {
      containerClassName.push("items-center");
    }

    containerClassName.push("md:flex-row md:items-end");

    if (isBasketUi) {
      containerClassName.push("lg:flex-col lg:gap-y-0");
    }

    containerClassName.push("md:items-center md:gap-y-4");
  }

  return (
    <div
      className={twMerge(
        "flex-1-1-0 flex flex-col items-center justify-between relative top-0 left-0 w-full px-4 py-2 sm:min-h-20 sm:flex-row sm:gap-4 overflow-hidden",
        className
      )}
    >
      {title && (
        <h3 className="text-white text-2xl lg:text-lg wideScreen:text-2xl font-semibold uppercase">
          {title}
        </h3>
      )}

      {isPriceSwitcherVisible && (
        <JourneySwitch isHorizontal={isMobile} isUIv2={isUIv2} />
      )}

      <div className={twMerge(containerClassName)}>
        {getContent()}

        {isFinanceButtonVisible &&
          (isUIv2 ? (
            <FinanceWidgetButtonUIv2 isBasketUi={isBasketUi} />
          ) : (
            <FinanceWidgetButton />
          ))}
      </div>
      {withParallelograms && !isSCFJourneySelected && !isUIv2 && (
        <Parallelograms
          className={twMerge(
            "absolute right-0",
            isParallelogramsTemporarilyDisabled && "lg:hidden min-[67rem]:block"
          )}
        />
      )}
    </div>
  );
};

PriceDetail.propTypes = {
  currencySymbol: string.isRequired,
  journey: string.isRequired,
  onIsFinancingOpenChange: func.isRequired,
  priceFormat: string.isRequired,
  amount: number,
  className: string,
  deposit: number,
  forceFinancingSwitch: bool,
  isBasketUi: bool,
  isFinanceRequestError: bool,
  isLoading: bool,
  isPersonalisedVACOffer: bool,
  isQuantityVisible: bool,
  teaser: financeGatewayTeaserType,
  total: number,
  vehicleQuantity: number,
  withDeliveryInfo: bool,
  withEcoBonus: bool,
  withFinanceWidgetButton: bool,
  withJourneySwitch: bool,
  withParallelograms: bool,
  withTitle: bool,
};

export default PriceDetail;
